import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Multiselect } from '@/components/ui/multiselect';
import { Switch } from '@/components/ui/switch';
import {
  useBackOfficeUsersService,
  useAdminUpdateWorkspaceService,
} from '@/services/backoffice';
import type { BackOfficeWorkspaceFeature, FeatureName } from '@ditto/shared';
import { X } from 'lucide-react';
import { useEffect, useMemo, useState, type FC } from 'react';
import { toast } from 'sonner';

const FEATURE_LABELS: Record<FeatureName, string> = {
  chat: 'Chat',
  regulatoryWatch: 'Regulatory Watch',
  tableView: 'Table Views',
  literatureReview: 'Literature Review',
  mcp: 'MCP',
};

const ALL_FEATURES: FeatureName[] = [
  'chat',
  'regulatoryWatch',
  'tableView',
  'literatureReview',
  'mcp',
];

interface WorkspaceEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspace: {
    id: string;
    name: string;
    uname: string;
    features?: BackOfficeWorkspaceFeature[];
  } | null;
}

export const WorkspaceEditModal: FC<WorkspaceEditModalProps> = ({
  open,
  onOpenChange,
  workspace,
}) => {
  const { users } = useBackOfficeUsersService();
  const { updateWorkspace, isPending } = useAdminUpdateWorkspaceService();

  const [workspaceName, setWorkspaceName] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [featureStates, setFeatureStates] = useState<
    Record<FeatureName, boolean>
  >({
    chat: false,
    regulatoryWatch: false,
    tableView: false,
    literatureReview: false,
    mcp: false,
  });

  // Initialize when workspace changes
  useEffect(() => {
    if (workspace) {
      setWorkspaceName(workspace.name);
      const workspaceUserIds = users
        .filter(user => user.workspaceIds?.includes(workspace.id))
        .map(user => user.id);
      setSelectedUserIds(workspaceUserIds);

      const states: Record<FeatureName, boolean> = {
        chat: false,
        regulatoryWatch: false,
        tableView: false,
        literatureReview: false,
        mcp: false,
      };
      workspace.features?.forEach(f => {
        if (f.feature in states) {
          states[f.feature as FeatureName] = f.status === 'ACTIVATED';
        }
      });
      setFeatureStates(states);
    } else {
      setWorkspaceName('');
      setSelectedUserIds([]);
      setFeatureStates({
        chat: false,
        regulatoryWatch: false,
        tableView: false,
        literatureReview: false,
        mcp: false,
      });
    }
  }, [workspace, users]);

  const userOptions = useMemo(
    () =>
      users.map(user => ({
        value: user.id,
        label:
          `${user.firstName || ''} ${user.lastName || ''} (${user.email || ''})`.trim(),
      })),
    [users]
  );

  const selectedUsers = useMemo(() => {
    return users.filter(user => selectedUserIds.includes(user.id));
  }, [users, selectedUserIds]);

  const handleSave = () => {
    if (!workspace) return;

    const features = ALL_FEATURES.map(feature => ({
      feature,
      status: featureStates[feature]
        ? ('ACTIVATED' as const)
        : ('DEACTIVATED' as const),
    }));

    updateWorkspace(
      {
        workspaceId: workspace.id,
        name: workspaceName.trim() || undefined,
        memberIds: selectedUserIds,
        features,
      },
      {
        onSuccess: () => {
          toast.success('Workspace updated successfully');
          onOpenChange(false);
        },
        onError: (error: any) => {
          const errorMessage =
            error?.message ||
            error?.response?.body?.message ||
            error?.response?.data?.message ||
            'Failed to update workspace';
          toast.error(errorMessage);
        },
      }
    );
  };

  const removeUser = (userId: string) => {
    setSelectedUserIds(prev => prev.filter(id => id !== userId));
  };

  const customTrigger = (
    <div className="min-h-[40px] w-full border rounded-md px-3 py-2 bg-background">
      {selectedUsers.length === 0 ? (
        <span className="text-muted-foreground">Select users...</span>
      ) : (
        <div className="flex flex-wrap gap-1">
          {selectedUsers.map(user => (
            <div
              key={user.id}
              className="inline-flex items-center gap-1 px-2 py-1 bg-secondary text-secondary-foreground rounded-md text-sm"
            >
              <span>
                {user.firstName} {user.lastName}
              </span>
              <button
                type="button"
                onPointerDown={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  removeUser(user.id);
                }}
                className="hover:bg-secondary-foreground/20 rounded-sm p-0.5"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (!workspace) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{`Edit ${workspace.name}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          {/* Workspace name */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Workspace Name</Label>
            <Input
              value={workspaceName}
              onChange={e => setWorkspaceName(e.target.value)}
              placeholder="Workspace name"
            />
          </div>

          {/* Users section */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Users</label>
            <Multiselect
              options={userOptions}
              selected={selectedUserIds}
              onChange={setSelectedUserIds}
              placeholder="Select users..."
              showSearch
              showClearAll
              contentClassName="max-h-[300px] overflow-y-auto w-full"
              trigger={customTrigger}
            />
            <p className="text-xs text-muted-foreground">
              {selectedUserIds.length === 0
                ? 'No users selected'
                : selectedUserIds.length === 1
                  ? `${selectedUserIds.length} user selected`
                  : `${selectedUserIds.length} users selected`}
            </p>
          </div>

          {/* Feature flags section */}
          <div className="space-y-3">
            <label className="text-sm font-medium">Feature Flags</label>
            <div className="space-y-2">
              {ALL_FEATURES.map(feature => (
                <div
                  key={feature}
                  className="flex items-center justify-between"
                >
                  <Label className="text-sm">{FEATURE_LABELS[feature]}</Label>
                  <Switch
                    checked={featureStates[feature]}
                    onCheckedChange={checked =>
                      setFeatureStates(prev => ({
                        ...prev,
                        [feature]: checked,
                      }))
                    }
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isPending}>
              Save Changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
